import { Injectable } from '@angular/core';

import { RidangoEpic, RidangoReduxAction } from '@ridango/core';

import { ActionsObservable } from 'redux-observable';
import { catchError, map, switchMap } from 'rxjs/operators';

import { NotificationFacade } from '../../notifications/+state/notification.facade';
import { ShoppingCartService } from '../shopping-cart.service';
import { ShoppingCartActionsV2 } from './shopping-cart-v2.actions';
import { ShoppingCartActions } from './shopping-cart.actions';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root'
})
export class ShoppingCartEpics implements RidangoEpic {
    constructor(
        private actions: ShoppingCartActions,
        private actionsV2: ShoppingCartActionsV2,
        private service: ShoppingCartService,
        private notification: NotificationFacade,
        private translate: TranslateService
    ) {}

    register() {
        return [this.postPayloads, this.getShoppingCartContents, this.removeTicketFromCart, this.deleteTicketFromCart];
    }

    postPayloads = (action$: ActionsObservable<any>) =>
        action$.ofType(ShoppingCartActionsV2.POST_TICKET_PAYLOADS).pipe(
            switchMap((action: RidangoReduxAction) => {
                // NB! payload is entire purchase flow state object
                return this.service.postPayloads(action.payload, action.payload.language).pipe(
                    map((response: any) => {
                        const failedTickets = response.tickets.filter((t) => {
                            if (t.ticket_creation_error) {
                                return true;
                            }
                            return false;
                        });

                        if (failedTickets.length === 0) {
                            const statePayload: any = {};
                            statePayload.shopping_cart_id = response.shopping_cart_id;
                            statePayload.status = 'S';
                            // normalizing tickets and adding required objects from purchase flow
                            statePayload.tickets = response.tickets
                                .map((t) => {
                                    if (t.start_zone_name && t.end_zone_name) {
                                        t.created_ticket.start_zone_name = t.start_zone_name;
                                        t.created_ticket.end_zone_name = t.end_zone_name;
                                    }
                                    return t.created_ticket;
                                })
                                .map((t) => {
                                    t.product = { ...action.payload.product };
                                    t.product.price = t.price;
                                    return t;
                                })
                                .reduce((acc, val) => {
                                    return { ...acc, [val.doc_num]: val };
                                }, {});
                            statePayload.docNums = response.tickets.map((t) => t.created_ticket.doc_num);
                            return this.actionsV2.postTicketPayloadsSuccess(statePayload);
                        }
                        this.notification.error(
                            this.translate.instant(
                                failedTickets.find((t) => t.ticket_creation_error)?.ticket_creation_error
                            )
                        );
                        window.scroll(0, 0);
                        return this.actionsV2.postTicketPayloadsFailed(failedTickets);
                    }),
                    catchError((e) => [this.actionsV2.postTicketPayloadsFailed(e)])
                );
            })
        );

    getShoppingCartContents = (action$: ActionsObservable<any>) =>
        action$.ofType(ShoppingCartActionsV2.FETCH_CART_CONTENTS).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.getCartContentsById(action.payload).pipe(
                    map((response) => this.actions.updateShoppingCart(response)),
                    catchError((e) => [this.actionsV2.postTicketPayloadsFailed(e)])
                );
            })
        );

    deleteTicketFromCart = (action$: ActionsObservable<any>) =>
        action$.ofType(ShoppingCartActionsV2.DELETE_TICKET_FROM_CART).pipe(
            switchMap((action: RidangoReduxAction) => {
                const { ticket, cart } = action.payload;
                return this.service.deleteTicketFromCart(ticket, cart).pipe(
                    map(() => this.actionsV2.removeTicketFromCart(ticket, cart)),
                    catchError((e) => [this.actionsV2.postTicketPayloadsFailed(e)])
                );
            })
        );

    removeTicketFromCart = (action$: ActionsObservable<any>) =>
        action$.ofType(ShoppingCartActionsV2.REMOVE_TICKET_FROM_CART).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.getCartContentsById(action.payload.cart.shopping_cart_id).pipe(
                    map((response) => this.actions.updateShoppingCart(response)),
                    catchError((e) => [this.actionsV2.removeTicketFromCartFailed(e)])
                );
            })
        );
}
